<template>
  <div class="bl-client-session bg-slate-200">
    <div class="bl-client-session-navigation-panel">
      <div
        v-if="pageLoading"
        class="absolute z-20 w-full h-full -m-4 -mr-5 lg:-my-6 lg:-mx-3 rounded-3xl"
      >
        <div
          class="absolute w-full h-full bg-pure-white opacity-75 rounded-3xl"
        ></div>
      </div>
      <button
        @click.prevent="selectedMenu = 'draft'"
        :class="{
          'bl-client-session-navigation-active': selectedMenu === 'draft'
        }"
        class="bl-client-session-navigation"
      >
        <BlTextSubtitle2
          v-if="selectedMenu === 'draft'"
          class="flex-shrink-0"
          text="Draft"
        />
        <BlTextBody1 v-else class="flex-shrink-0" text="Draft" />
        <BlBubbleCount
          v-if="draftSessions"
          :type="selectedMenu === 'session' ? 'secondary' : ''"
          class="bl-client-session-navigation-count"
          :count="draftSessionsCount || 0"
        />
        <div
          v-if="selectedMenu === 'draft'"
          class="bl-client-session-navigation-border"
        ></div>
      </button>
      <button
        @click="selectedMenu = 'session'"
        :class="{
          'bl-client-session-navigation-active': selectedMenu === 'session'
        }"
        class="bl-client-session-navigation"
      >
        <BlTextSubtitle2
          v-if="selectedMenu === 'session'"
          class="flex-shrink-0"
          text="Past Sessions"
        />
        <BlTextBody1 v-else class="flex-shrink-0" text="Past Sessions" />
        <BlBubbleCount
          :type="selectedMenu !== 'session' ? 'secondary' : ''"
          class="bl-client-session-navigation-count"
          :count="pastSessionsCount || 0"
        />
        <div
          v-if="selectedMenu === 'session'"
          class="bl-client-session-navigation-border"
        ></div>
      </button>
    </div>

    <div
      class="bl-client-session-panel"
      :style="selectedMenu === 'session' ? 'gap: 1.5rem' : ''"
    >
      <div class="bl-client-session-header">
        <div class="bl-client-session-header-name-container">
          <div class="bl-client-session-header-name">
            <BlTextTitle4
              :text="selectedMenu === 'draft' ? 'Draft' : 'Past Sessions'"
            />
            <BlBubbleCount
              v-if="sessions && sessions.length"
              :count="
                selectedMenu === 'draft'
                  ? draftSessionsCount || 0
                  : pastSessionsCount || 0
              "
              class=""
            />
          </div>
          <BlTextBody2
            :text="
              selectedMenu === 'draft'
                ? 'The drafts feature allows you to efficiently plan and prepare for your upcoming therapy sessions.'
                : 'Access the data from your past therapy sessions to gain insights and monitor progress.'
            "
            class="bl-client-session-header-description"
          />
        </div>
        <div
          v-if="selectedMenu === 'draft'"
          class="bl-client-session-header-action-container"
        >
          <BlInputButton
            class="bl-client-session-header-action"
            icon="plus"
            label="Create draft"
            :loading="createLoading"
            @clicked="createNewSession"
          />
        </div>
        <div
          v-if="selectedMenu === 'session'"
          class="bl-client-session-header-filter-container"
        >
          <div>
            <BlInputFilterAdvance
              bubbleCount
              @changed="inputHandler"
              :input="filterInput"
            />
          </div>
          <div>
            <BlInputSelect @changed="inputHandler" :input="sessionSortInput" />
          </div>
        </div>
      </div>

      <div
        v-if="selectedMenu === 'draft'"
        class="pb-4 overflow-x-auto scrollbar-lg"
        :style="{
          width: 'calc(100vw - 3rem - 20rem - 1.5rem - 5rem - 0.5rem)'
        }"
      >
        <div id="session-draft-header" class="flex bg-slate-200 rounded-t">
          <div
            class="flex border-t border-l border-r border-slate-400 sticky left-0 z-10 rounded-tl bg-pure-white"
          >
            <div class="px-4 py-3 flex-shrink-0 bg-slate-200 w-20 rounded-tl">
              <BlTextSubtitle3 text="ID" class="text-slate-700" />
            </div>
            <div class="px-4 py-3 flex flex-shrink-0 bg-slate-200 w-50">
              <BlTextSubtitle3 text="Name" class="text-slate-700" />
            </div>
          </div>
          <div
            class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-40"
          >
            <BlTextSubtitle3 text="Status" class="text-slate-700" />
          </div>
          <div
            class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-60"
          >
            <BlTextSubtitle3 text="Scheduled for" class="text-slate-700" />
          </div>
          <div
            class="pl-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-50"
          >
            <BlTextSubtitle3 text="Assigned therapist" class="text-slate-700" />
          </div>
          <div
            class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-40"
          >
            <BlTextSubtitle3 text="Number of targets" class="text-slate-700" />
          </div>
          <div class="flex sticky right-0 bg-pure-white flex-grow">
            <div
              class="px-4 py-3 border-t border-r border-slate-400 flex-shrink-0 bg-slate-200 w-60 rounded-tr flex-grow"
            ></div>
          </div>
        </div>

        <div
          v-if="fetchSessionDraftLoading"
          class="sticky left-0 h-100 w-full grid place-items-center flex-grow rounded-b border-l border-r border-b border-slate-400"
        >
          <div class="blum-loader"></div>
        </div>
        <div
          v-else-if="!draftSessions.length"
          class="sticky left-0 h-100 w-full grid place-items-center flex-grow rounded-b border-l border-r border-b border-slate-400"
        >
          <div class="text-center">
            <BlTextBody1
              text="Currently, there are no session draft to display. Once you create a"
            />
            <div class="flex items-center justify-center gap-1 text-center">
              <BlTextBody1 text="new draft, it will appear here." />
              <BlInputButton
                type="plain"
                dense
                label="Create a new draft."
                @clicked="createNewSession"
              />
            </div>
          </div>
        </div>
        <div v-else>
          <SessionDraftItem
            v-for="(session, idx) in draftSessions"
            :key="session.id"
            :session="session"
            :idx="idx"
            :isLast="idx + 1 === draftSessions.length"
            @after-commit="refreshSessions('draft,ongoing')"
          />
        </div>

        <BlInputPagination
          v-if="draftSessionsCount > 25"
          class="mt-4 sticky left-0 z-10"
          :page="page"
          :perPage="perPage"
          :total="draftSessionsCount"
          @changed="onChangePageDraft"
          @changed-per-page="onChangePerPageDraft"
        />
      </div>

      <div
        v-if="selectedMenu === 'session'"
        class="pb-4 overflow-x-auto scrollbar-lg"
        :style="{
          width: 'calc(100vw - 3rem - 20rem - 1.5rem - 5rem - 0.5rem)'
        }"
      >
        <div id="session-past-header" class="flex bg-slate-200 rounded-t">
          <div
            class="flex border-t border-l border-r border-slate-400 sticky left-0 z-10 rounded-tl bg-pure-white"
          >
            <div class="px-4 py-3 flex-shrink-0 bg-slate-200 w-20 rounded-tl">
              <BlTextSubtitle3 text="ID" class="text-slate-700" />
            </div>
            <div class="px-4 py-3 flex flex-shrink-0 bg-slate-200 w-50">
              <BlTextSubtitle3 text="Name" class="text-slate-700" />
            </div>
          </div>
          <div
            class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-40"
          >
            <BlTextSubtitle3 text="Status" class="text-slate-700" />
          </div>
          <div
            class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-60"
          >
            <BlTextSubtitle3 text="Scheduled for" class="text-slate-700" />
          </div>
          <div
            class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-64"
          >
            <BlTextSubtitle3 text="Recorded at" class="text-slate-700" />
          </div>
          <div
            class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-40"
          >
            <BlTextSubtitle3 text="Duration" class="text-slate-700" />
          </div>
          <div
            class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-40"
          >
            <BlTextSubtitle3 text="Recorded by" class="text-slate-700" />
          </div>
          <div
            class="px-4 py-3 border-t border-slate-400 flex-shrink-0 bg-slate-200 w-40"
          >
            <BlTextSubtitle3 text="Number of targets" class="text-slate-700" />
          </div>
          <div class="flex sticky right-0 bg-pure-white flex-grow">
            <div
              class="px-4 py-3 border-t border-r border-slate-400 flex-shrink-0 bg-slate-200 w-40 rounded-tr flex-grow"
            ></div>
          </div>
        </div>

        <div
          v-if="fetchSessionLoading"
          class="sticky left-0 h-100 w-full grid place-items-center flex-grow rounded-b border-l border-r border-b border-slate-400"
        >
          <div class="blum-loader"></div>
        </div>
        <div
          v-else-if="!pastSessions.length"
          class="sticky left-0 h-100 w-full grid place-items-center flex-grow rounded-b border-l border-r border-b border-slate-400"
        >
          <div class="text-center">
            <BlTextBody1
              text="Currently, there are no past session to display. Once you finish a"
            />
            <br />
            <BlTextBody1 text="session, it will appear here." />
          </div>
        </div>
        <div v-else>
          <SessionPastItem
            v-for="(session, idx) in pastSessions"
            :key="session.id"
            :session="session"
            :idx="idx"
            :isLast="idx + 1 === pastSessions.length"
            @after-commit="refreshSessions('completed,cancelled')"
          />
        </div>

        <BlInputPagination
          v-if="pastSessionsCount > 25"
          class="mt-4 sticky left-0 z-10"
          :page="page"
          :perPage="perPage"
          :total="pastSessionsCount"
          @changed="onChangePagePast"
          @changed-per-page="onChangePerPagePast"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import SessionDraftItem from "./session/DraftItem.vue";
import SessionPastItem from "./session/PastItem.vue";

import BlTextTitle4 from "../BlTextTitle4.vue";
import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextSubtitle3 from "../BlTextSubtitle3.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";

import BlInputFilter from "../BlInputFilter.vue";
import BlInputFilterAdvance from "../BlInputFilterAdvance.vue";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputSelect from "../BlInputSelect.vue";
import BlInputPagination from "../BlInputPagination.vue";

export default {
  name: "ClientSession",
  components: {
    SessionDraftItem,
    SessionPastItem,
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextSubtitle3,
    BlTextBody1,
    BlTextBody2,
    BlInputFilter,
    BlInputFilterAdvance,
    BlBubbleCount,
    BlInputButton,
    BlInputSelect,
    BlInputPagination
  },
  props: ["id"],
  data() {
    return {
      pageLoading: true,
      createLoading: false,
      sessionTimeout: null,
      filterInput: {
        model: {
          date: null,
          therapist: []
        },
        tabs: [
          { name: "Date", kind: "date" },
          { name: "Therapist", kind: "therapist", options: [] }
        ]
      },
      sessionSortInput: {
        model: "Most recent",
        prefix: "Sort by",
        options: [
          "Most recent",
          "Oldest",
          "Duration - longest",
          "Duration - shortest"
        ]
      },
      fetchSessionDraftLoading: true,
      fetchSessionLoading: true,
      draftSessionsCount: 0,
      pastSessionsCount: 0,
      selectedMenu: "draft",
      page: 1,
      perPage: 25
    };
  },
  computed: {
    ...mapGetters(["client", "users", "targets", "sessions", "sessionDrafts"]),
    draftSessions() {
      return this.sessionDrafts;
    },
    pastSessions() {
      return this.sessions;
    },
    sessionFilter() {
      return this.filterInput.model;
    },
    sessionSort() {
      let str = "";
      switch (this.sessionSortInput.model) {
        case "Most recent":
          str = "newest";
          break;
        case "Oldest":
          str = "oldest";
          break;
        case "Duration - longest":
          str = "duration_longest";
          break;
        case "Duration - shortest":
          str = "duration_shortest";
          break;
      }
      return str;
    }
  },
  watch: {
    users(val) {
      if (val.length) {
        this.filterInput.tabs[1].options = val.map(a => ({
          name: a.name,
          value: a.id
        }));
      }
    },
    sessionFilter: {
      handler() {
        clearTimeout(this.sessionTimeout);
        this.sessionTimeout = setTimeout(
          this.refreshSessions("completed,cancelled"),
          500
        );
      },
      deep: true
    },
    sessionSort() {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = setTimeout(
        this.refreshSessions("completed,cancelled"),
        500
      );
    },
    selectedMenu() {
      this.onChangePageDraft(1);
      this.onChangePagePast(1);
    }
  },
  async mounted() {
    this.pageLoading = true;
    this.filterInput.tabs[1].options = this.users.map(a => ({
      name: a.name,
      value: a.id
    }));
    await this.refreshSessions();
    this.pageLoading = false;
  },
  methods: {
    ...mapActions(["getSessions", "createSession"]),
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    async createNewSession() {
      try {
        this.createLoading = true;
        const data = {
          client_id: this.id,
          session: {
            name: "Unnamed session",
            status: "draft"
          }
        };

        const res = await this.createSession(data);
        this.createLoading = false;
        if (res.success) {
          this.$router.push({ path: `/session/${res.data.slug}/edit` });
        }
      } catch (error) {
        this.createLoading = false;
        console.error(error);
      }
    },
    onChangePageDraft(page) {
      if (page === this.page) return;
      this.page = page;
      this.refreshSessions("draft");
    },
    onChangePerPageDraft(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.refreshSessions("draft");
    },
    onChangePagePast(page) {
      if (page === this.page) return;
      this.page = page;
      this.refreshSessions("completed,cancelled");
    },
    onChangePerPagePast(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.refreshSessions("completed");
    },
    async refreshSessions(status = "draft,ongoing,completed,cancelled") {
      const filterBy =
        this.sessionFilter.date !== "custom" ? this.sessionFilter.date : "";
      const startDate =
        this.sessionFilter.date && this.sessionFilter.date.start_date
          ? this.sessionFilter.date.start_date
          : "";
      const endDate =
        this.sessionFilter.date && this.sessionFilter.date.end_date
          ? this.sessionFilter.date.end_date
          : "";
      let userIds = "";
      if (
        this.sessionFilter &&
        this.sessionFilter.therapist &&
        this.sessionFilter.therapist.length
      ) {
        userIds = this.sessionFilter.therapist.join(",");
      }

      if (status.includes("completed") || status.includes("cancelled")) {
        let params = `?page=${this.page}&per_page=${this.perPage}&status=completed,cancelled&sort=${this.sessionSort}`;
        if (filterBy) params += `&filter_by=${filterBy}`;
        if (startDate) params += `&start_date=${startDate}`;
        if (endDate) params += `&end_date=${endDate}`;
        if (userIds) params += `&user_ids=${userIds}`;
        this.fetchSessionLoading = true;
        await this.getSessions({ id: this.id, params }).then(
          ({ success, data }) => {
            this.fetchSessionLoading = false;
            if (!success) return;
            this.pastSessionsCount = data.total_count;
          }
        );
      }

      if (status.includes("draft") || status.includes("ongoing")) {
        let params = `?page=${this.page}&per_page=${this.perPage}&status=draft,ongoing`;
        this.fetchSessionDraftLoading = true;
        await this.getSessions({ id: this.id, params }).then(
          ({ success, data }) => {
            this.fetchSessionDraftLoading = false;
            if (!success) return;
            this.draftSessionsCount = data.total_count;
          }
        );
      }
    }
  }
};
</script>

<style scoped lang="scss">
.bl-client-session {
  position: relative;
  width: 100%;
  padding: 1.5rem;
  box-sizing: border-box;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 1.5rem;
  .bl-client-session-navigation-panel {
    position: relative;
    width: 20rem;
    padding: 1.5rem 0.75rem;
    border: 1px solid var(--border-color);
    border-radius: 1.25rem;
    box-sizing: border-box;
    background: var(--background-depth-one-color);
    display: flex;
    flex-direction: column;
    gap: 1rem;
    button.bl-client-session-navigation {
      cursor: pointer;
      position: relative;
      width: 100%;
      height: 3rem;
      padding: 0 1rem;
      border: none;
      background: var(--background-depth-one-color);
      box-sizing: border-box;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      .bl-client-session-navigation-count {
        position: absolute;
        right: 1rem;
      }
      .bl-client-session-navigation-border {
        position: absolute;
        right: 0;
        width: 0.25rem;
        height: 100%;
        border-radius: 0.125rem;
        background-color: var(--primary-color);
      }
      &:focus {
        outline: none;
      }
    }
  }
  .bl-client-session-panel {
    position: relative;
    width: calc(100vw - 3rem - 20rem - 1.5rem - 0.5rem);
    height: 100%;
    padding: 2.5rem;
    border: 1px solid var(--border-color);
    border-radius: 1.25rem;
    box-sizing: border-box;
    background: var(--background-depth-one-color);
    display: flex;
    flex-direction: column;
    gap: 1rem;
    .bl-client-session-header {
      position: relative;
      width: 100%;
      margin-bottom: 1.5rem;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;
      .bl-client-session-header-name-container {
        position: relative;
        display: flex;
        flex-direction: column;
        .bl-client-session-header-name {
          position: relative;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          .bl-client-session-header-count {
            position: relative;
            width: 1.5rem;
            height: 1.5rem;
            border-radius: 0.75rem;
            background-color: var(--primary-color);
            margin-left: 0.5rem;
            font-weight: 600;
            font-size: 0.75rem;
            line-height: 1;
            color: #fff;
            display: flex;
            justify-content: center;
            align-items: center;
          }
        }
        .bl-client-session-header-description {
          position: relative;
          color: var(--font-secondary-color);
          margin-top: 0.25rem;
        }
      }
      .bl-client-session-header-filter-container {
        position: relative;
        width: 100%;
        height: 2.5rem;
        margin-top: 1rem;
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        &::after {
          content: "";
          position: relative;
          left: -2.5rem;
          width: calc(100% + 5rem);
          height: 1px;
          background: var(--border-color);
          margin-top: 1.5rem;
          flex-shrink: 0;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/clients/ClientSession.vue